import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ToteService, WaveService } from '../utility';
import { useDebouncedEffect } from './debounce';
import type { ToteLabelDataType } from 'types/warehoue/label';

export const ItemAllocationToteDetails = allocationId => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({});
  const [error, setError] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await WaveService.getAllocationTote(allocationId);
      setData(res?.data);
    } catch (error) {
      setError(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (allocationId) {
      fetchData();
    } else {
      setData({});
    }
  }, [allocationId]);

  return { data, loading, error };
};

export const ToteListWarehouseHook = (
  warehouseId,
  status = 'ready_to_pack',
  shouldFetch = true,
  search = '',
) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [error, setError] = useState(null);
  const [totalData, setTotalData] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [refreshKey, setRefreshKey] = useState(0);
  const [filter, setFilter] = useState({
    limit: 10,
    page: 1,
  });

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = {
        ...filter,
      } as any;

      if (status) {
        params.status = status;
      }

      if (params.search) {
        params.search = params.search.trim();
      }

      const res = await WaveService.toteList(warehouseId, params);
      setData(res?.data?.totes || []);
      setTotalData(res?.total);
      setTotalPages(res?.totalPages);
    } catch (error) {
      setError(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    filterChanged('search', search);
  }, [search]);

  useDebouncedEffect(
    () => {
      if (warehouseId && shouldFetch) fetchData();
    },
    [filter, warehouseId, refreshKey, shouldFetch],
    300,
  );

  const pageChanged = page => {
    const temp = {
      ...filter,
      page,
    };
    setFilter({ ...temp });
  };

  const filterChanged = (key, value) => {
    const temp = {
      ...filter,
      page: 1,
      [key]: value,
    };
    setFilter({ ...temp });
  };

  const refresh = () => {
    setRefreshKey(refreshKey => refreshKey + 1);
  };

  return {
    data,
    loading,
    error,
    totalData,
    totalPages,
    filter,
    pageChanged,
    filterChanged,
    refresh,
  };
};

const TOTE_FEED_QUERY_KEY = ['totes', 'feed'] as const;

export type ToteFeedType = 'tote-upload' | 'tote-label-export';

export const ToteFeedHook = (warehouseId: string, type: ToteFeedType = 'tote-upload') => {
  const [refreshKey, setRefreshKey] = useState(0);
  const [filter, setFilter] = useState({ limit: 10, page: 1 });
  const [debouncedFilter, setDebouncedFilter] = useState({ limit: 10, page: 1 });

  useDebouncedEffect(
    () => {
      if (filter.limit && filter.page) {
        setDebouncedFilter(filter);
      }
    },
    [filter],
    500,
  );

  const queryKey = [
    ...TOTE_FEED_QUERY_KEY,
    warehouseId,
    type,
    debouncedFilter,
    refreshKey,
  ];

  const {
    data: queryData,
    isFetching,
    error: queryError,
  } = useQuery({
    queryKey,
    queryFn: async () => {
      const res = await ToteService.getFeedLogs({
        ...debouncedFilter,
        warehouseId,
        type,
      });
      return {
        feeds: res?.data?.feeds ?? [],
        totalCount: res?.totalCount ?? 0,
        totalPages: res?.totalPages ?? 0,
      };
    },
    enabled: !!warehouseId,
  });

  const data = queryData?.feeds ?? [];
  const totalData = queryData?.totalCount ?? 0;
  const totalPages = queryData?.totalPages ?? 0;
  const error = queryError ?? null;

  useEffect(() => {
    setFilter(f => ({ ...f, page: 1 }));
  }, [warehouseId, type]);

  const pageChanged = (page: number) => {
    setFilter(f => ({ ...f, page }));
  };

  const filterChanged = (key: string, value: unknown) => {
    setFilter(f => ({ ...f, page: 1, [key]: value }));
  };

  const refresh = () => setRefreshKey(k => k + 1);

  return {
    data,
    error,
    filter,
    filterChanged,
    loading: isFetching,
    pageChanged,
    totalData,
    totalPages,
    refresh,
  };
};

export const toteLimboCheckHook = containerId => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({});
  const [error, setError] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = {
        type: 'tote',
        containerId,
      };
      const res = await ToteService.checkLimboState(params);
      setData(res?.data);
    } catch (error) {
      setError(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (containerId) {
      fetchData();
    }
  }, [containerId]);

  return { data, loading, error };
};

type LabelHistoryResponse = {
  data?: ToteLabelDataType;
};

type PreviousToteLabelHookReturnType = {
  loading: boolean;
  error: string | null;
  data?: ToteLabelDataType;
};

export const PreviousToteLabelHook = (
  warehouseId?: string,
  toteNumber?: string,
): PreviousToteLabelHookReturnType => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<ToteLabelDataType>();
  const [error, setError] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = {
        toteNumbers: toteNumber,
        warehouseId,
      };
      const res: LabelHistoryResponse = await ToteService.getPreviousLabel(params);
      setData(res?.data);
    } catch (error) {
      setError(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (warehouseId && toteNumber) {
      fetchData();
    }
  }, [warehouseId, toteNumber]);

  return { data, loading, error };
};
